import React from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";

// Import layout components
import Footer from "../components/layout/Footer";
import Header from "../components/layout/Header";

// Import page components
import About from "../pages/About";
import ApplicationDetails from "../pages/ApplicationDetails";
import ApplicationForm from "../pages/ApplicationForm";
import ChangePassword from "../pages/ChangePassword";
import CourseDetail from "../pages/CourseDetail";
import Courses from "../pages/Courses";
import EmailVerification from "../pages/EmailVerification";
import FAQ from "../pages/FAQ";
import ForgotPassword from "../pages/ForgotPassword";
import LandingPage from "../pages/LandingPage";
import MyApplications from "../pages/MyApplications";
import PaymentCancel from "../pages/PaymentCancel";
import PaymentCompletion from "../pages/PaymentCompletion";
import PaymentSuccess from "../pages/PaymentSuccess";
import Pricing from "../pages/Pricing";
import PrivacyPolicy from "../pages/PrivacyPolicy";
import Profile from "../pages/Profile";
import RefundPolicy from "../pages/RefundPolicy";
import Register from "../pages/Register";
import ResetPassword from "../pages/ResetPassword";
import Services from "../pages/Services";
import Settings from "../pages/Settings";
import SignIn from "../pages/SignIn";
import TermsOfService from "../pages/TermsOfService";
import University from "../pages/University";
import UniversityDetail from "../pages/UniversityDetail";
import Wishlist from "../pages/Wishlist";

// Import admin pages
import {
  AdminApplicationDetails,
  AdminDashboard,
  AdminSignIn,
  ApplicationManagement,
  DocumentManagement,
  UserDetails,
  UserManagement,
} from "../pages/admin";
import CourseManagement from "../pages/admin/CourseManagement";
import UniversityManagement from "../pages/admin/UniversityManagement";

// Import auth context
import { AuthProvider, useAuth } from "../contexts/AuthContext";
import { ToastProvider } from "../contexts/ToastContext";
import ScrollToTop from "./ScrollToTop";

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return <>{children}</>;
};

// Admin Protected Route Component (requires ADMIN role)
const AdminProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/signin" replace />;
  }

  if (user?.role !== "ADMIN") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirects to landing page if already authenticated, except for auth pages)
const PublicRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  // Only redirect authenticated users away from auth pages (signin, register, etc.)
  const authPages = [
    "/signin",
    "/register",
    "/forgot-password",
    "/reset-password",
    "/verify-email",
  ];
  const isAuthPage = authPages.some((page) =>
    location.pathname.startsWith(page)
  );

  if (isAuthenticated && isAuthPage) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Layout wrapper for authenticated pages
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="min-h-screen bg-gray-50">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);

// Layout wrapper for public pages
interface PublicLayoutProps {
  children: React.ReactNode;
  showHeaderFooter?: boolean;
}

const PublicLayout: React.FC<PublicLayoutProps> = ({
  children,
  showHeaderFooter = false,
}) => (
  <div className="min-h-screen bg-white">
    {showHeaderFooter && <Header />}
    <main>{children}</main>
    {showHeaderFooter && <Footer />}
  </div>
);

// Routes component (needs to be inside AuthProvider)
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <PublicLayout>
            <LandingPage />
          </PublicLayout>
        }
      />

      <Route
        path="/signin"
        element={
          <PublicRoute>
            <SignIn />
          </PublicRoute>
        }
      />

      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />

      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />

      <Route
        path="/verify-email"
        element={
          <PublicRoute>
            <EmailVerification />
          </PublicRoute>
        }
      />

      <Route
        path="/courses"
        element={
          <PublicRoute>
            <PublicLayout showHeaderFooter>
              <Courses />
            </PublicLayout>
          </PublicRoute>
        }
      />

      <Route
        path="/courses/:id"
        element={
          <PublicLayout showHeaderFooter>
            <CourseDetail />
          </PublicLayout>
        }
      />

      <Route
        path="/universities"
        element={
          <PublicLayout showHeaderFooter>
            <University />
          </PublicLayout>
        }
      />

      <Route
        path="/universities/:id"
        element={
          <PublicLayout showHeaderFooter>
            <UniversityDetail />
          </PublicLayout>
        }
      />

      <Route
        path="/about"
        element={
          <PublicLayout showHeaderFooter>
            <About />
          </PublicLayout>
        }
      />

      <Route
        path="/services"
        element={
          <PublicLayout showHeaderFooter>
            <Services />
          </PublicLayout>
        }
      />

      {/* Policy Pages */}
      <Route
        path="/privacy-policy"
        element={
          <PublicLayout showHeaderFooter>
            <PrivacyPolicy />
          </PublicLayout>
        }
      />

      <Route
        path="/terms-of-service"
        element={
          <PublicLayout showHeaderFooter>
            <TermsOfService />
          </PublicLayout>
        }
      />

      <Route
        path="/refund-policy"
        element={
          <PublicLayout showHeaderFooter>
            <RefundPolicy />
          </PublicLayout>
        }
      />

      <Route
        path="/faq"
        element={
          <PublicLayout showHeaderFooter>
            <FAQ />
          </PublicLayout>
        }
      />

      <Route
        path="/pricing"
        element={
          <PublicLayout showHeaderFooter>
            <Pricing />
          </PublicLayout>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Profile />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Settings />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ChangePassword />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/wishlist"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Wishlist />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Application Routes */}
      <Route
        path="/apply/:courseId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ApplicationForm />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-applications"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <MyApplications />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/applications/:id"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ApplicationDetails />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Payment Routes */}
      <Route path="/payment/success" element={<PaymentCompletion />} />

      <Route path="/payment/completion" element={<PaymentCompletion />} />

      <Route
        path="/payment/cancel"
        element={
          <ProtectedRoute>
            <PaymentCancel />
          </ProtectedRoute>
        }
      />

      <Route path="/payment/failure" element={<PaymentCancel />} />

      {/* Legacy protected payment success route for authenticated users */}
      <Route
        path="/payment/success-authenticated"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route path="/admin/signin" element={<AdminSignIn />} />

      <Route
        path="/admin"
        element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/admin/users"
        element={
          <AdminProtectedRoute>
            <UserManagement />
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/admin/users/:id"
        element={
          <AdminProtectedRoute>
            <UserDetails />
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/admin/applications"
        element={
          <AdminProtectedRoute>
            <ApplicationManagement />
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/admin/applications/:id"
        element={
          <AdminProtectedRoute>
            <AdminApplicationDetails />
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/admin/documents"
        element={
          <AdminProtectedRoute>
            <DocumentManagement />
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/admin/universities"
        element={
          <AdminProtectedRoute>
            <UniversityManagement />
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/admin/courses"
        element={
          <AdminProtectedRoute>
            <CourseManagement />
          </AdminProtectedRoute>
        }
      />

      {/* Fallback route */}
      <Route
        path="*"
        element={
          <PublicLayout showHeaderFooter>
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                <p className="text-gray-600 mb-8">Page not found</p>
                <a
                  href="/"
                  className="bg-cyan-600 text-white px-6 py-3 rounded-lg hover:bg-cyan-700 transition-colors"
                >
                  Go Home
                </a>
              </div>
            </div>
          </PublicLayout>
        }
      />
    </Routes>
  );
};

const AppRouter: React.FC = () => {
  return (
    <ToastProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ToastProvider>
  );
};

export default AppRouter;
